/**
 * Who owns the identity-scoped values in this browser's sessionStorage (KAN-623).
 *
 * Two caches are scoped to a person rather than to a tab: the in-progress checkout draft (plus its
 * UTM attribution) and the "this phone was verified" flag that lets booking skip an OTP. Both must
 * be dropped the moment a different person signs in, or one customer's session leaks into another.
 *
 * ── Why the Firebase UID, and not the email ──
 *
 * Both caches used to be keyed on `user.email`, and every comparison was guarded on that email
 * being truthy:
 *
 *     if (storedOwner && incomingEmail && storedOwner !== incomingEmail) { clear(); }
 *     if (incomingEmail) { setItem(OWNER_KEY, incomingEmail); }
 *
 * A phone-first homeowner has no email, so `incomingEmail` is null and BOTH branches are skipped.
 * The comparison does not fail — it silently no-ops, which means the check fails **OPEN**: the new
 * person inherits the previous person's checkout draft, their UTM attribution, and their
 * verified-phone flag. The owner key is never rewritten either, so the staleness persists.
 *
 * `firebase_id` is non-null for every account type and is already the system's identity of record,
 * so the guards are unnecessary and the check can fail **CLOSED**: no usable id means clear.
 *
 * ── Legacy values migrate themselves ──
 *
 * Sessions from before this change hold an email in the owner key. An email can never equal a UID,
 * so it reads as a mismatch and is cleared on the first load after deploy. Losing one in-progress
 * draft once is correct; carrying it across identities is not.
 */

/**
 * Trimmed, but deliberately NOT lowercased.
 *
 * Emails needed case-insensitive comparison. Firebase UIDs are case-SENSITIVE, so folding case
 * would compare something other than the identity itself. Equality of the same UID is unaffected
 * either way; this just avoids widening the comparison for no reason.
 */
function normalizeOwner(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

/**
 * Does the stored owner belong to somebody other than the current user?
 *
 * Fails CLOSED. A missing or blank `firebaseId` counts as foreign whenever anything is stored,
 * because we cannot show that the cache belongs to whoever is here now — and the cost of being
 * wrong is one lost draft versus leaking a session.
 *
 * Nothing stored is not a mismatch: there is no previous owner to differ from.
 */
export function isForeignOwner(
  storedOwner: string | null | undefined,
  firebaseId: string | null | undefined,
): boolean {
  const stored = normalizeOwner(storedOwner);
  if (!stored) return false;

  const current = normalizeOwner(firebaseId);
  if (!current) return true;

  return stored !== current;
}

/** Records the current owner, or removes the key entirely when there is no usable id. */
export function rememberOwner(
  storageKey: string,
  firebaseId: string | null | undefined,
): void {
  const current = normalizeOwner(firebaseId);
  if (!current) {
    // Never leave a stale owner behind: an unattributable cache must read as foreign to the
    // next person, which is what an absent key guarantees.
    sessionStorage.removeItem(storageKey);
    return;
  }
  sessionStorage.setItem(storageKey, current);
}
